// --- Dependencies -------------------------------------------------------------------------------
import { Pagination } from "@mui/material";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Skeleton from "@/UI/atoms/Skeleton";
import EditableCell from "@/UI/inputs/EditableCell";
import type { Props } from "./types";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

const SKELETON_ROWS = 8;

/** Below this a row cannot hold its full column set and still be read without scrolling. */
const COMPACT_WIDTH = 700;

/** Fits the two round row controls plus their gap, and stays the same in the header where they are absent. */
const ACTIONS_WIDTH = "76px";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The content wrapper every list screen renders into: one table, its four states, selection, inline
 * editing and paging.
 *
 * Built on CSS grid rather than a real `<table>` so a column can be given a flexible track and the
 * whole row can still be one focusable unit. The semantics are put back by hand with `role` attributes
 * — a grid that reads as a list to a screen reader would be a regression on the cards it replaces.
 *
 * Selection is held by the screen, not here. The bulk bar needs the same ids to act on, and a table
 * that owned them would have to hand them upward on every change anyway.
 */
export default function DataTable<T>({
    rows,
    columns: allColumns,
    rowId,
    loading,
    error,
    onRetry,
    emptyTitle,
    emptyHint,
    pager,
    selection,
    onCellSave,
    rowActions,
}: Props<T>) {
    const t = useTranslation();

    /**
     * Columns marked `secondary` are dropped outright on a narrow screen rather than hidden in CSS. The
     * grid template is built from this list, so removing a column has to shorten the track list too —
     * `display: none` would leave its slot behind and every column after it would take the wrong width.
     */
    const [isCompact, setIsCompact] = useState(() => typeof window !== "undefined" && window.innerWidth <= COMPACT_WIDTH);

    useEffect(() => {
        const query = window.matchMedia(`(max-width: ${COMPACT_WIDTH}px)`);
        const sync = () => setIsCompact(query.matches);
        sync();
        query.addEventListener("change", sync);
        return () => query.removeEventListener("change", sync);
    }, []);

    const columns = useMemo(() => (isCompact ? allColumns.filter((column) => !column.secondary) : allColumns), [allColumns, isCompact]);

    /**
     * One grid template shared by the header and every row, so the two can never drift apart. Handed to
     * CSS as a custom property on the wrapper rather than as an inline `grid-template-columns` on each
     * row: the track list is data and cannot live in the stylesheet, but this way the *property* still
     * does, and it is written once instead of once per row.
     */
    const template = useMemo(() => {
        const parts = columns.map((column) => column.width ?? "minmax(120px, 1fr)");
        if (selection) parts.unshift("36px");
        /**
         * A fixed track, never `auto`. The header's actions cell is empty, so `auto` resolved to 0px
         * there and to the buttons' width in the rows — and because the remaining tracks are flexible,
         * that difference was redistributed across all of them and knocked every header off its column.
         */
        if (rowActions) parts.push(ACTIONS_WIDTH);
        return parts.join(" ");
    }, [columns, selection, rowActions]);

    const ids = rows.map(rowId);
    const allSelected = ids.length > 0 && ids.every((id) => selection?.selected.includes(id));

    const toggleAll = () => selection?.onChange(allSelected ? [] : ids);

    const toggleOne = (id: string) => {
        if (!selection) return;
        const next = selection.selected.includes(id) ? selection.selected.filter((one) => one !== id) : [...selection.selected, id];
        selection.onChange(next);
    };

    // --- States ---------------------------------------------------------------------------------

    if (loading)
        return (
            <div className="data-table" aria-busy="true">
                <div className="data-table-scroll">
                    <div className="data-table-rows">
                        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                            <Skeleton key={index} className="data-table-skeleton" />
                        ))}
                    </div>
                </div>
            </div>
        );

    if (error)
        return (
            <div className="data-table">
                <div className="data-table-state">
                    <p className="data-table-state-title">{error}</p>
                    <button type="button" className="data-table-retry" onClick={onRetry}>
                        {t.table.retry}
                    </button>
                </div>
            </div>
        );

    if (!rows.length)
        return (
            <div className="data-table">
                <div className="data-table-state">
                    <p className="data-table-state-title">{emptyTitle ?? t.table.empty}</p>
                    <p className="data-table-state-hint">{emptyHint ?? t.table.emptyHint}</p>
                </div>
            </div>
        );

    // --- Table ----------------------------------------------------------------------------------

    return (
        <div className="data-table">
            {/* The only horizontal scroller on the page — a wide table scrolls inside its own card
                rather than dragging the whole dashboard sideways. */}
            <div className="data-table-scroll">
                <div className="data-table-grid" role="table" style={{ "--data-table-template": template } as CSSProperties}>
                    <div className="data-table-head" role="row">
                        {selection && (
                            <span className="data-table-cell data-table-cell--check" role="columnheader">
                                <input
                                    type="checkbox"
                                    className="data-table-check"
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    aria-label={t.table.selectAll}
                                />
                            </span>
                        )}
                        {columns.map((column) => (
                            <span
                                key={column.key}
                                className="data-table-cell data-table-head-cell"
                                data-align={column.align ?? "start"}
                                role="columnheader"
                            >
                                {column.label}
                            </span>
                        ))}
                        {rowActions && <span className="data-table-cell" role="columnheader" aria-label={t.table.actions} />}
                    </div>

                    <div className="data-table-rows" role="rowgroup">
                        {rows.map((row) => {
                            const id = rowId(row);
                            const isSelected = Boolean(selection?.selected.includes(id));

                            return (
                                <div key={id} className="data-table-row" data-selected={isSelected} role="row">
                                    {selection && (
                                        <span className="data-table-cell data-table-cell--check" role="cell">
                                            <input
                                                type="checkbox"
                                                className="data-table-check"
                                                checked={isSelected}
                                                onChange={() => toggleOne(id)}
                                                aria-label={t.table.selectRow}
                                            />
                                        </span>
                                    )}

                                    {columns.map((column) => (
                                        <span key={column.key} className="data-table-cell" data-align={column.align ?? "start"} role="cell">
                                            {column.edit && onCellSave ? (
                                                <EditableCell
                                                    value={column.edit.value(row)}
                                                    display={column.render?.(row)}
                                                    config={column.edit}
                                                    /* A selected row is edited through the bulk bar, so the two
                                                       never race to write the same field from two places. */
                                                    disabled={isSelected}
                                                    onSave={(value) => onCellSave(row, column.key, value)}
                                                />
                                            ) : (
                                                (column.render?.(row) ?? "—")
                                            )}
                                        </span>
                                    ))}

                                    {rowActions && (
                                        <span className="data-table-cell data-table-cell--actions" role="cell">
                                            {rowActions(row)}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {pager && (
                <div className="data-table-pager">
                    {/* MUI owns the page maths — which numbers to show, where the ellipses fall — so the
                        row stays one line at any page count instead of growing with the collection. */}
                    <Pagination
                        dir="ltr"
                        count={pager.lastPage}
                        page={pager.page}
                        onChange={(_, next) => pager.onPage(next)}
                        siblingCount={1}
                        boundaryCount={1}
                        shape="rounded"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                fontFamily: "Alexandria, sans-serif",
                                fontSize: "var(--font-xs)",
                                color: "var(--color-gray-03)",
                                borderRadius: "var(--radius-lev3)",
                                minWidth: "30px",
                                height: "30px",
                            },
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: "var(--color-brand)",
                                color: "var(--color-white)",
                            },
                            "& .MuiPaginationItem-root.Mui-selected:hover": { backgroundColor: "var(--color-brand)" },
                            "& .MuiPaginationItem-previousNext": {
                                backgroundColor: "var(--color-light)",
                                borderRadius: "var(--radius-full)",
                            },
                        }}
                    />

                    {/* "N of M" — the page size beside the collection total, as in the reference. */}
                    <span className="data-table-count">
                        <span className="data-table-count-page">{rows.length}</span>
                        <span className="data-table-count-total">
                            {t.table.countOf} {pager.total}
                        </span>
                    </span>
                </div>
            )}
        </div>
    );
}
